// The error report of the client (fault-reports.md §2, R1582).
//
// A page that a player opens installs one `error` handler and one
// `unhandledrejection` handler on the window. Each uncaught fault becomes
// one error report: the message, the stack, the build, the page, the
// renderer backend, the device class and, on the game page, the world. The
// handler sends it to `POST /api/error-reports` of the meta server with a
// `keepalive` fetch, so a report of a page that is closing still leaves.
// The meta server reads the account off the session cookie, and the short
// user agent off the request.
//
// The rules:
//
// - **It never throws, and it never reports its own failure.** Every step
//   stands in a `try`, a failed send is dropped without a word, and a fault
//   inside the handler is not a fault it reports.
// - **It reports the faults of the page alone.** It drops an error of
//   another realm and a fault whose top frame names a browser extension
//   (R3196).
// - **One fault goes once.** The handler drops a repeat of one fault in one
//   page load, and it holds a token bucket of `FAULT_CLIENT_BURST` reports,
//   then one each `FAULT_CLIENT_REFILL_SEC` seconds. The meta server holds
//   its own buckets above it.
// - **It cuts, the meta server checks.** The message and the stack are cut
//   to the caps of `[meta.faults]`, in code points, which the meta server
//   counts as runes.
// - **It holds no sentence and no figure of its own.** Every cap comes from
//   `gen/balance.ts`.
//
// The page's `main.ts` imports `net/faultboot.ts` second, after the boot
// error painter: that module installs the handlers when it evaluates, so a
// fault in the evaluation of a later module is reported too. This module
// itself has no effect on import, so a test or a render module may import
// `noteFaultContext` freely.

import { cutRunes } from '../util/text';
import {
  FAULT_CLIENT_BURST, FAULT_CLIENT_REFILL_SEC, FAULT_MESSAGE_MAX_RUNES, FAULT_STACK_MAX_RUNES,
  FAULT_TAG_MAX_LEN,
} from '../gen/balance';
import { randomHex } from '../util/random';
import { storageStore, takeRenamed } from './renamed';
import { storeOf, writeStore } from '../ui/localstore';
import { coarsePointer } from '../ui/pointer';

/** The route of the meta server. */
export const ERROR_REPORT_PATH = '/api/error-reports';

/** The storage key of the session id: one id for one browser tab. */
export const FAULT_SESSION_KEY = 'ap.fault.session';
/** The old name of the key, before the short form `ap` (R1676). */
export const OLD_FAULT_SESSION_KEY = 'aq.fault.session';

/** The faults one page load remembers, to drop a repeat. */
const MAX_SEEN = 100;

/** The stack lines the repeat key reads. */
const KEY_LINES = 3;

/** Chrome reports a benign layout notice through `window.onerror`, as the
 *  boot error painter knows. A cross-origin script reports a fault with no
 *  detail at all; neither is a fault of the game. */
const BENIGN = /^(ResizeObserver loop|Script error\.?$)/;

/** A frame of a script that a browser extension put into the page: Chrome,
 *  Firefox and Safari name the scheme of the extension (R3196). */
const EXTENSION_FRAME = /(?:chrome|moz|safari|safari-web)-extension:\/\//;

/** One error report, the body of the route. */
export interface ErrorReport {
  session: string;
  kind: 'error' | 'rejection';
  message: string;
  stack: string;
  page: string;
  build: string;
  backend: string;
  device: string;
  world: number;
}

/** What the page tells the reporter as it goes: the renderer backend once
 *  the engine stands, and the world on the game page. */
export interface FaultContext {
  backend: string;
  world: number;
}

/** What a reporter needs from the browser. A test passes its own. */
export interface FaultDeps {
  /** Sends one body. It must not throw; a failure is dropped. */
  send(body: string): void;
  /** The clock, in milliseconds. */
  now(): number;
  session: string;
  page: string;
  build: string;
  device(): string;
}

/** The context every reporter of the page reads. */
const context: FaultContext = { backend: '', world: 0 };

/** Notes a part of the context: the engine notes its backend, the game page
 *  its world. A value that is not a short token is dropped. */
export function noteFaultContext(part: Partial<FaultContext>): void {
  try {
    if (part.backend !== undefined) context.backend = tag(part.backend);
    if (part.world !== undefined && Number.isInteger(part.world) && part.world >= 0) context.world = part.world;
  } catch { /* the context stays as it was */ }
}

/** A short field: letters, digits, `.`, `_` and `-`, cut to the cap. */
export function tag(s: string): string {
  return s.replace(/[^A-Za-z0-9._-]/g, '').slice(0, FAULT_TAG_MAX_LEN);
}

/** The page of a path: `/` and `/index.html` are the game page, which holds
 *  the landing, the hub and the map; every other page is its own name. */
export function pageOf(path: string): string {
  const last = path.replace(/\/+$/, '').split('/').pop() ?? '';
  const stem = tag(last.replace(/\.html$/, ''));
  return stem === '' || stem === 'index' ? 'game' : stem;
}

/** The build of the page: the file name of its entry chunk, which the
 *  bundler names by a content hash, for example `index-B4x9kQ_m`. A page
 *  the dev server serves reads `dev`. */
export function buildOf(src: string): string {
  if (!src.includes('/assets/')) return 'dev';
  const file = src.split('?')[0].split('/').pop() ?? '';
  return tag(file.replace(/\.m?js$/, '')) || 'dev';
}

/** The device class: a fine pointer is a desktop, and a coarse pointer is a
 *  phone or a tablet by the short side of the screen. */
export function deviceClass(coarse: boolean, shortSide: number): string {
  if (!coarse) return 'desktop';
  return shortSide < 600 ? 'phone' : 'tablet';
}

/** The message and the stack of a thrown value. */
function describe(
  value: unknown, fallback: string,
): { message: string; stack: string; foreign?: true } {
  if (value instanceof Error) {
    return { message: `${value.name}: ${value.message}`, stack: value.stack ?? '' };
  }
  // An error of another realm fails `instanceof Error` and keeps its tag:
  // the isolated world of an extension's content script makes one, and no
  // module of the page does (R3196).
  if (Object.prototype.toString.call(value) === '[object Error]') {
    return { message: '', stack: '', foreign: true };
  }
  if (value !== undefined && value !== null) {
    let s = '';
    try {
      s = typeof value === 'string' ? value : String(value);
    } catch { /* an object with a throwing toString */ }
    if (s !== '') return { message: s, stack: '' };
  }
  return { message: fallback, stack: '' };
}

/** A reporter: the repeat set, the bucket and the two handlers. */
export class FaultReporter {
  private readonly seen = new Set<string>();
  private tokens = FAULT_CLIENT_BURST;
  private last: number;
  /** True while the reporter handles a fault, so a fault inside it is
   *  never a fault it reports. */
  private busy = false;

  constructor(private readonly deps: FaultDeps) {
    this.last = deps.now();
  }

  /** The `error` handler of the window. */
  readonly onError = (ev: Event): void => {
    try {
      const e = ev as ErrorEvent;
      const where = e.filename ? `at ${e.filename}:${e.lineno ?? 0}:${e.colno ?? 0}` : '';
      const d = describe(e.error, e.message ?? '');
      if (d.foreign) return;
      this.report('error', d.message, d.stack || where);
    } catch { /* nothing is left to report to */ }
  };

  /** The `unhandledrejection` handler of the window. */
  readonly onRejection = (ev: Event): void => {
    try {
      const d = describe((ev as PromiseRejectionEvent).reason, 'unhandled rejection');
      if (d.foreign) return;
      this.report('rejection', d.message, d.stack);
    } catch { /* nothing is left to report to */ }
  };

  /** Builds and sends one report, unless it is benign, a fault of a
   *  browser extension, a repeat, or over the bucket. It answers whether it
   *  sent. */
  report(kind: ErrorReport['kind'], message: string, stack: string): boolean {
    if (this.busy) return false;
    this.busy = true;
    try {
      const msg = cutRunes(message.trim(), FAULT_MESSAGE_MAX_RUNES);
      if (msg === '' || BENIGN.test(msg)) return false;
      const top = stack.split('\n').find((l) => l.trim() !== '') ?? '';
      if (EXTENSION_FRAME.test(top)) return false;
      const key = [msg, ...stack.split('\n').slice(0, KEY_LINES + 1)].join('\n');
      if (this.seen.has(key) || this.seen.size >= MAX_SEEN) return false;
      if (!this.spend()) return false;
      this.seen.add(key);
      const body: ErrorReport = {
        session: this.deps.session, kind, message: msg,
        stack: cutRunes(stack, FAULT_STACK_MAX_RUNES),
        page: this.deps.page, build: this.deps.build, backend: context.backend,
        device: this.deps.device(), world: context.world,
      };
      this.deps.send(JSON.stringify(body));
      return true;
    } catch {
      return false;
    } finally {
      this.busy = false;
    }
  }

  /** Spends one token of the bucket, after the refill since the last call. */
  private spend(): boolean {
    const now = this.deps.now();
    this.tokens = Math.min(FAULT_CLIENT_BURST,
      this.tokens + (now - this.last) / (FAULT_CLIENT_REFILL_SEC * 1000));
    this.last = now;
    if (this.tokens < 1) return false;
    this.tokens -= 1;
    return true;
  }
}

/** The session id of this tab: the stored one, or a new one. */
export function faultSessionId(): string {
  // A private window or blocked storage reads null and keeps no id: an id
  // for this page load.
  const stored = takeRenamed(storageStore(storeOf('session')), FAULT_SESSION_KEY, OLD_FAULT_SESSION_KEY) ?? '';
  if (/^[0-9a-f]{32}$/.test(stored)) return stored;
  let id: string;
  try {
    id = randomHex(16);
  } catch {
    return '0'.repeat(32);
  }
  writeStore(FAULT_SESSION_KEY, id, 'session');
  return id;
}

/** Sends one body with a `keepalive` fetch, or a beacon where the browser
 *  has no fetch. A failure is dropped. */
function sendBody(body: string): void {
  try {
    if (typeof fetch === 'function') {
      void fetch(ERROR_REPORT_PATH, {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body,
      }).catch(() => { /* dropped */ });
      return;
    }
    navigator.sendBeacon?.(ERROR_REPORT_PATH, new Blob([body], { type: 'application/json' }));
  } catch { /* dropped */ }
}

/** The build of this page, read off its module script. The REPORT sheet
 *  states it on its fixed line (R1626). */
export function pageBuild(): string {
  try {
    const src = document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? '';
    return buildOf(src);
  } catch {
    return 'dev';
  }
}

/** The device class of this browser. */
function browserDevice(): string {
  try {
    return deviceClass(coarsePointer(), Math.min(screen.width, screen.height));
  } catch {
    return '';
  }
}

let installed: FaultReporter | null = null;

/** Puts the two handlers on the window, once. A second call does nothing.
 *  It never throws. */
export function installFaultReports(): void {
  try {
    if (installed !== null || typeof window === 'undefined') return;
    installed = new FaultReporter({
      send: sendBody, now: () => Date.now(), session: faultSessionId(),
      page: pageOf(location.pathname), build: pageBuild(), device: browserDevice,
    });
    window.addEventListener('error', installed.onError);
    window.addEventListener('unhandledrejection', installed.onRejection);
  } catch { /* the page runs without reports */ }
}
